/**
 * Outcome Driver Store
 *
 * Manages workspace-level outcome driver artifacts produced by the
 * Outcome Driver Generator tool. Each driver captures: context of
 * purpose, current state, problem/opportunity, ramifications, and
 * a synthesized narrative.
 *
 * Drivers can be grouped into named sets and optionally scoped
 * to a DEGAP cycle.
 *
 * Per Build Contract: Supabase is source of truth. Zustand is a cache.
 * All writes go to the database first.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  createInitialSyncState,
  generateUUID,
} from '../../lib/supabaseSync';
import type { SyncState } from '../../lib/supabaseSync';

// ── Types ─────────────────────────────────────────────────────

export interface OutcomeDriver {
  id: string;
  workspaceId: string;
  title: string;
  contextOfPurpose: string;
  currentState: string;
  problemOpportunity: string;
  ramifications: string;
  narrative: string;
  cycleId: string | null;
  /** Optional: Strategy whose opportunity space this driver names ("names the opportunity space for"). */
  strategyId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OutcomeDriverSet {
  id: string;
  workspaceId: string;
  title: string;
  cycleId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OutcomeDriverSetItem {
  id: string;
  setId: string;
  driverId: string;
  position: number;
}

export interface CreateDriverInput {
  title?: string;
  contextOfPurpose?: string;
  currentState?: string;
  problemOpportunity?: string;
  ramifications?: string;
  narrative?: string;
  cycleId?: string | null;
  strategyId?: string | null;
}

export interface CreateSetInput {
  title: string;
  cycleId?: string | null;
  driverIds?: string[];
}

// ── Store interface ───────────────────────────────────────────

interface OutcomeDriverState {
  drivers: OutcomeDriver[];
  sets: OutcomeDriverSet[];
  setItems: OutcomeDriverSetItem[];
  syncState: SyncState;

  fetchFromSupabase: () => Promise<void>;

  createDriver: (input: CreateDriverInput) => Promise<{ driver: OutcomeDriver | null; error?: string }>;
  updateDriver: (id: string, updates: Partial<CreateDriverInput>) => Promise<{ success: boolean; error?: string }>;
  deleteDriver: (id: string) => Promise<{ success: boolean; error?: string }>;

  createSet: (input: CreateSetInput) => Promise<{ set: OutcomeDriverSet | null; error?: string }>;
  updateSet: (id: string, updates: { title?: string; cycleId?: string | null }) => Promise<{ success: boolean; error?: string }>;
  deleteSet: (id: string) => Promise<{ success: boolean; error?: string }>;

  addDriverToSet: (setId: string, driverId: string) => Promise<{ success: boolean; error?: string }>;
  removeDriverFromSet: (setId: string, driverId: string) => Promise<{ success: boolean; error?: string }>;

  getDriverById: (id: string) => OutcomeDriver | undefined;
  getSetById: (id: string) => OutcomeDriverSet | undefined;
  getDriversForSet: (setId: string) => OutcomeDriver[];
  getDriversForCycle: (cycleId: string) => OutcomeDriver[];
}

// ── Helpers ───────────────────────────────────────────────────

function rowToDriver(row: Record<string, unknown>): OutcomeDriver {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    title: (row.title as string) ?? '',
    contextOfPurpose: (row.context_of_purpose as string) ?? '',
    currentState: (row.current_state as string) ?? '',
    problemOpportunity: (row.problem_opportunity as string) ?? '',
    ramifications: (row.ramifications as string) ?? '',
    narrative: (row.narrative as string) ?? '',
    cycleId: (row.cycle_id as string) ?? null,
    strategyId: (row.strategy_id as string) ?? null,
    createdBy: (row.created_by as string) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function rowToSet(row: Record<string, unknown>): OutcomeDriverSet {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    title: (row.title as string) ?? '',
    cycleId: (row.cycle_id as string) ?? null,
    createdBy: (row.created_by as string) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function rowToSetItem(row: Record<string, unknown>): OutcomeDriverSetItem {
  return {
    id: row.id as string,
    setId: row.set_id as string,
    driverId: row.driver_id as string,
    position: (row.position as number) ?? 0,
  };
}

// ── Store ─────────────────────────────────────────────────────

export const useOutcomeDriverStore = create<OutcomeDriverState>()((set, get) => ({
  drivers: [],
  sets: [],
  setItems: [],
  syncState: createInitialSyncState(),

  fetchFromSupabase: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });

    try {
      const [driversRes, setsRes, setItemsRes] = await Promise.all([
        supabase
          .from('outcome_drivers')
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false }),
        supabase
          .from('outcome_driver_sets')
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false }),
        supabase
          .from('outcome_driver_set_items')
          .select('*, set:outcome_driver_sets!inner(workspace_id)')
          .eq('outcome_driver_sets.workspace_id', workspaceId),
      ]);

      set({
        drivers: (driversRes.data ?? []).map((r) => rowToDriver(r as Record<string, unknown>)),
        sets: (setsRes.data ?? []).map((r) => rowToSet(r as Record<string, unknown>)),
        setItems: (setItemsRes.data ?? []).map((r) => rowToSetItem(r as Record<string, unknown>)),
        syncState: {
          isLoading: false,
          syncError: driversRes.error?.message ?? setsRes.error?.message ?? null,
          lastSyncAt: new Date().toISOString(),
        },
      });
    } catch (err) {
      console.error('[OutcomeDriverStore] fetchFromSupabase error:', err);
      set({ syncState: { isLoading: false, syncError: String(err), lastSyncAt: null } });
    }
  },

  createDriver: async (input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { driver: null, error: 'No Supabase client or workspace' };

    const { data, error } = await supabase
      .from('outcome_drivers')
      .insert({
        id: generateUUID(),
        workspace_id: workspaceId,
        title: input.title ?? '',
        context_of_purpose: input.contextOfPurpose ?? '',
        current_state: input.currentState ?? '',
        problem_opportunity: input.problemOpportunity ?? '',
        ramifications: input.ramifications ?? '',
        narrative: input.narrative ?? '',
        cycle_id: input.cycleId ?? null,
        strategy_id: input.strategyId ?? null,
      })
      .select('*')
      .single();

    if (error) {
      console.error('[OutcomeDriverStore] createDriver error:', error);
      return { driver: null, error: error.message };
    }

    const driver = rowToDriver(data as Record<string, unknown>);
    set((state) => ({ drivers: [driver, ...state.drivers] }));
    return { driver };
  },

  updateDriver: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.contextOfPurpose !== undefined) dbUpdates.context_of_purpose = updates.contextOfPurpose;
    if (updates.currentState !== undefined) dbUpdates.current_state = updates.currentState;
    if (updates.problemOpportunity !== undefined) dbUpdates.problem_opportunity = updates.problemOpportunity;
    if (updates.ramifications !== undefined) dbUpdates.ramifications = updates.ramifications;
    if (updates.narrative !== undefined) dbUpdates.narrative = updates.narrative;
    if (updates.cycleId !== undefined) dbUpdates.cycle_id = updates.cycleId;
    if (updates.strategyId !== undefined) dbUpdates.strategy_id = updates.strategyId;

    // Optimistic update
    set((state) => ({
      drivers: state.drivers.map((d) =>
        d.id === id ? { ...d, ...updates } : d
      ),
    }));

    const { error } = await supabase
      .from('outcome_drivers')
      .update(dbUpdates)
      .eq('id', id);

    if (error) {
      console.error('[OutcomeDriverStore] updateDriver error:', error);
      await get().fetchFromSupabase();
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  deleteDriver: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const prev = get().drivers;
    set((state) => ({ drivers: state.drivers.filter((d) => d.id !== id) }));

    const { error } = await supabase.from('outcome_drivers').delete().eq('id', id);
    if (error) {
      console.error('[OutcomeDriverStore] deleteDriver error:', error);
      set({ drivers: prev });
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  createSet: async (input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { set: null, error: 'No Supabase client or workspace' };

    const setId = generateUUID();
    const { data, error } = await supabase
      .from('outcome_driver_sets')
      .insert({
        id: setId,
        workspace_id: workspaceId,
        title: input.title,
        cycle_id: input.cycleId ?? null,
      })
      .select('*')
      .single();

    if (error) {
      console.error('[OutcomeDriverStore] createSet error:', error);
      return { set: null, error: error.message };
    }

    const newSet = rowToSet(data as Record<string, unknown>);
    set((state) => ({ sets: [newSet, ...state.sets] }));

    // Wire initial drivers if provided
    if (input.driverIds?.length) {
      const items = input.driverIds.map((driverId, i) => ({
        id: generateUUID(),
        set_id: setId,
        driver_id: driverId,
        position: i,
      }));
      const { data: itemData, error: itemError } = await supabase
        .from('outcome_driver_set_items')
        .insert(items)
        .select('*');
      if (!itemError && itemData) {
        set((state) => ({
          setItems: [
            ...state.setItems,
            ...(itemData as Record<string, unknown>[]).map(rowToSetItem),
          ],
        }));
      }
    }

    return { set: newSet };
  },

  updateSet: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.cycleId !== undefined) dbUpdates.cycle_id = updates.cycleId;

    set((state) => ({
      sets: state.sets.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    }));

    const { error } = await supabase.from('outcome_driver_sets').update(dbUpdates).eq('id', id);
    if (error) {
      console.error('[OutcomeDriverStore] updateSet error:', error);
      await get().fetchFromSupabase();
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  deleteSet: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const prevSets = get().sets;
    const prevItems = get().setItems;
    set((state) => ({
      sets: state.sets.filter((s) => s.id !== id),
      setItems: state.setItems.filter((i) => i.setId !== id),
    }));

    const { error } = await supabase.from('outcome_driver_sets').delete().eq('id', id);
    if (error) {
      console.error('[OutcomeDriverStore] deleteSet error:', error);
      set({ sets: prevSets, setItems: prevItems });
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  addDriverToSet: async (setId, driverId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const maxPos = get().setItems
      .filter((i) => i.setId === setId)
      .reduce((max, i) => Math.max(max, i.position), -1);

    const newItem = {
      id: generateUUID(),
      set_id: setId,
      driver_id: driverId,
      position: maxPos + 1,
    };

    const { data, error } = await supabase
      .from('outcome_driver_set_items')
      .insert(newItem)
      .select('*')
      .single();

    if (error) {
      console.error('[OutcomeDriverStore] addDriverToSet error:', error);
      return { success: false, error: error.message };
    }

    set((state) => ({
      setItems: [...state.setItems, rowToSetItem(data as Record<string, unknown>)],
    }));
    return { success: true };
  },

  removeDriverFromSet: async (setId, driverId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const prev = get().setItems;
    set((state) => ({
      setItems: state.setItems.filter((i) => !(i.setId === setId && i.driverId === driverId)),
    }));

    const { error } = await supabase
      .from('outcome_driver_set_items')
      .delete()
      .eq('set_id', setId)
      .eq('driver_id', driverId);

    if (error) {
      console.error('[OutcomeDriverStore] removeDriverFromSet error:', error);
      set({ setItems: prev });
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  getDriverById: (id) => get().drivers.find((d) => d.id === id),
  getSetById: (id) => get().sets.find((s) => s.id === id),

  getDriversForSet: (setId) => {
    const { drivers, setItems } = get();
    const items = setItems
      .filter((i) => i.setId === setId)
      .sort((a, b) => a.position - b.position);
    return items
      .map((i) => drivers.find((d) => d.id === i.driverId))
      .filter(Boolean) as OutcomeDriver[];
  },

  getDriversForCycle: (cycleId) =>
    get().drivers.filter((d) => d.cycleId === cycleId),
}));
